import { ApiRequestError } from '@/api/client';
import { envelopeMessage } from '@/api/envelope';
import { http } from '@/api/http';

export type InvoiceFieldType = 'text' | 'email' | 'tel' | 'number' | 'select' | 'textarea';

export type InvoiceFieldOption = {
  value: string;
  label: string;
};

export type InvoiceFormField = {
  key: string;
  label: string;
  type: InvoiceFieldType;
  required: boolean;
  placeholder?: string;
  hint?: string;
  defaultValue?: string;
  options?: InvoiceFieldOption[];
  maxLength?: number;
  pattern?: string;
  patternMessage?: string;
  min?: number;
  max?: number;
  readOnly?: boolean;
  group?: string;
};

export type InvoiceCountryConfig = {
  code: string;
  label: string;
  fields: InvoiceFormField[];
  /** 发票上显示的供应商税号标签（如 ABN / NZBN / EIN / VAT Registration Number） */
  supplierTaxLabel?: string;
};

export type InvoiceSettings = {
  country: string;
  values: Record<string, string>;
  countries: InvoiceCountryConfig[];
};

const META_KEYS = new Set([
  '_countries',
  'countries',
  'invoice_info',
  'invoiceInfo',
  'country_list',
  'countryList',
]);

function readString(o: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = o[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return undefined;
}

function isRequired(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true' || value === 'yes';
}

function isReadOnly(o: Record<string, unknown>): boolean {
  return isRequired(o.readonly) || isRequired(o.read_only) || isRequired(o.readOnly) || isRequired(o.disabled);
}

function fieldType(raw: string | undefined): InvoiceFieldType {
  const value = (raw ?? 'text').trim().toLowerCase();
  if (value === 'email') return 'email';
  if (value === 'tel' || value === 'phone') return 'tel';
  if (value === 'number' || value === 'decimal' || value === 'rate' || value === 'percent') return 'number';
  if (value === 'select' || value === 'dropdown' || value === 'radio') return 'select';
  if (value === 'textarea' || value === 'multiline') return 'textarea';
  return 'text';
}

function readOptions(raw: unknown): InvoiceFieldOption[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const options: InvoiceFieldOption[] = [];
  for (const item of raw) {
    if (typeof item === 'string' || typeof item === 'number') {
      const value = String(item);
      options.push({ value, label: value });
      continue;
    }
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    const value = readString(o, ['value', 'code', 'key', 'id']);
    if (!value) continue;
    options.push({ value, label: readString(o, ['label', 'name', 'title']) ?? value });
  }
  return options.length > 0 ? options : undefined;
}

function parseField(raw: unknown): InvoiceFormField | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const key = readString(o, ['key', 'name', 'field', 'prop', 'id']);
  const label = readString(o, ['label', 'title']) ?? key;
  if (!key || !label) return null;
  const options = readOptions(o.options ?? o.enum ?? o.choices);
  let type = fieldType(readString(o, ['type', 'component', 'input']));
  if (options && type === 'text') type = 'select';
  const maxLength = typeof o.maxLength === 'number'
    ? o.maxLength
    : typeof o.max_length === 'number'
      ? o.max_length
      : typeof o.maxlength === 'number'
        ? o.maxlength
        : undefined;
  const min = typeof o.min === 'number' ? o.min : undefined;
  const max = typeof o.max === 'number' ? o.max : undefined;
  return {
    key,
    label,
    type,
    required: isRequired(o.required ?? o.is_required ?? o.isRequired),
    placeholder: readString(o, ['placeholder', 'place_holder']),
    hint: readString(o, ['hint', 'tips', 'tip', 'desc', 'description', 'help']),
    defaultValue: readString(o, ['default', 'default_value', 'defaultValue']),
    options,
    maxLength,
    pattern: readString(o, ['pattern', 'regex']),
    patternMessage: readString(o, ['pattern_message', 'patternMessage', 'error', 'error_message']),
    min,
    max,
    readOnly: isReadOnly(o),
    group: readString(o, ['group', 'section', 'group_name', 'groupName']),
  };
}

function parseFields(raw: unknown): InvoiceFormField[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(parseField).filter((field): field is InvoiceFormField => field !== null);
}

function rateText(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'string' && value.trim()) {
    const number = Number(value);
    if (Number.isFinite(number) && Number.isInteger(number)) return String(number);
    return value.trim();
  }
  return '';
}

function stringList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) => String(item).trim()).filter((item) => item.length > 0);
}

function metaRecord(raw: unknown): Record<string, unknown> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  return raw as Record<string, unknown>;
}

function metaFlag(value: unknown): boolean {
  return value === true || value === 1 || value === '1' || value === 'true';
}

/** `_countries` 没有 `fields` 时，用该国家自己的税号、税率和银行配置生成表单。 */
function fieldsFromCountryMeta(o: Record<string, unknown>): InvoiceFormField[] {
  const taxIdLabelFull = readString(o, ['tax_id_label', 'taxIdLabel']) ?? 'Tax ID';
  // 发票上显示的简短税号标签（如 ABN / NZBN / EIN），优先用于输入框 label
  const supplierTaxLabel = readString(o, ['supplier_tax_label', 'supplierTaxLabel']);
  const taxIdLabel = supplierTaxLabel ?? taxIdLabelFull;
  // 简短标签与完整说明不同时，将完整说明作为 hint 展示
  const taxIdHint = supplierTaxLabel && supplierTaxLabel !== taxIdLabelFull ? taxIdLabelFull : undefined;
  const taxName = readString(o, ['default_tax_name', 'defaultTaxName']) ?? '';
  const rate = rateText(o.default_tax_rate ?? o.defaultTaxRate);
  const branchLabel = readString(o, ['branch_code_label', 'branchCodeLabel']) ?? 'Bank Code';
  const addressLabel = readString(o, ['address_label', 'addressLabel']) ?? 'Registered Address';
  const phonePlaceholder = readString(o, ['phone_placeholder', 'phonePlaceholder']);
  const taxRateHint = readString(o, ['tax_rate_hint', 'taxRateHint']);
  const routingMode = (readString(o, ['routing_mode', 'routingMode']) ?? '').toLowerCase();
  const taxIdMeta = metaRecord(o.tax_id_field ?? o.taxIdField);
  const routingMeta = metaRecord(o.routing_field ?? o.routingField);
  const showVat = metaFlag(o.show_vat ?? o.showVat) || taxName.toUpperCase() === 'VAT';
  const showIban = metaFlag(o.show_iban ?? o.showIban) || taxName.toUpperCase() === 'VAT';
  const canada = routingMode === 'canada' || /institution|transit/i.test(branchLabel);
  const taxNames = stringList(o.tax_names ?? o.taxNames);
  if (taxName && !taxNames.includes(taxName)) taxNames.unshift(taxName);
  const taxNameEditable = metaFlag(o.tax_name_editable ?? o.taxNameEditable) && taxNames.length > 1;

  const fields: InvoiceFormField[] = [
    { key: 'legal_name', label: 'Legal Company Name', type: 'text', required: true, group: 'Business', placeholder: 'Registered legal name' },
    { key: 'address', label: addressLabel, type: 'text', required: true, group: 'Business', placeholder: 'Start typing to search addresses…' },
    {
      key: 'email',
      label: 'Email Address',
      type: 'email',
      required: true,
      group: 'Business',
      placeholder: 'billing@company.com',
      pattern: '^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$',
      patternMessage: 'Please enter a valid email address.',
    },
    { key: 'phone', label: 'Phone Number', type: 'tel', required: true, group: 'Business', placeholder: phonePlaceholder },
    {
      key: 'tax_id',
      label: taxIdLabel,
      type: 'text',
      required: true,
      group: 'Tax',
      placeholder: taxIdMeta ? readString(taxIdMeta, ['placeholder']) : undefined,
      hint: taxIdMeta ? readString(taxIdMeta, ['hint']) : taxIdHint,
      maxLength: taxIdMeta && typeof taxIdMeta.max_length === 'number' ? taxIdMeta.max_length : undefined,
      pattern: taxIdMeta ? readString(taxIdMeta, ['pattern']) : undefined,
      patternMessage: taxIdMeta ? readString(taxIdMeta, ['pattern_message', 'patternMessage']) : undefined,
    },
  ];
  if (showVat) {
    fields.push({
      key: 'vat_number',
      label: 'VAT Registration Number',
      type: 'text',
      required: false,
      group: 'Tax',
      placeholder: 'GB123456789',
      hint: 'Optional. Leave blank if the company is not VAT registered.',
    });
  }
  fields.push({
    key: 'tax_name',
    label: 'Tax Name',
    type: taxNameEditable ? 'select' : 'text',
    required: true,
    group: 'Tax',
    readOnly: !taxNameEditable,
    defaultValue: taxName,
    options: taxNameEditable ? taxNames.map((name) => ({ value: name, label: name })) : undefined,
  });
  fields.push({
    key: 'tax_rate',
    label: 'Rate (%)',
    type: 'number',
    required: true,
    group: 'Tax',
    placeholder: rate,
    hint: taxRateHint,
    defaultValue: rate,
    min: 0,
    max: typeof o.tax_rate_max === 'number' ? o.tax_rate_max : 100,
  });
  fields.push(
    { key: 'bank_name', label: 'Bank Name', type: 'text', required: false, group: 'Bank Account', placeholder: 'Bank name' },
    { key: 'account_name', label: 'Account Name', type: 'text', required: false, group: 'Bank Account', placeholder: 'Account holder name' },
  );
  if (canada) {
    fields.push(
      {
        key: 'institution_no',
        label: 'Institution No.',
        type: 'text',
        required: false,
        group: 'Bank Account',
        placeholder: '000',
        hint: '3-digit institution number and 5-digit transit number.',
        maxLength: 3,
        pattern: '^\\d{3}$',
        patternMessage: 'Institution number must be exactly 3 digits.',
      },
      {
        key: 'transit_no',
        label: 'Transit No.',
        type: 'text',
        required: false,
        group: 'Bank Account',
        placeholder: '00000',
        maxLength: 5,
        pattern: '^\\d{5}$',
        patternMessage: 'Transit number must be exactly 5 digits.',
      },
    );
  } else {
    fields.push({
      key: 'bank_code',
      label: branchLabel,
      type: 'text',
      required: false,
      group: 'Bank Account',
      placeholder: routingMeta ? readString(routingMeta, ['placeholder']) : undefined,
      hint: routingMeta ? readString(routingMeta, ['hint']) : undefined,
      pattern: routingMeta ? readString(routingMeta, ['pattern']) : undefined,
      patternMessage: routingMeta ? readString(routingMeta, ['pattern_message', 'patternMessage']) : undefined,
    });
  }
  fields.push({
    key: 'account_number',
    label: 'Account Number',
    type: 'text',
    required: false,
    group: 'Bank Account',
    placeholder: 'Account number',
  });
  if (showIban) {
    fields.push({
      key: 'iban',
      label: 'IBAN',
      type: 'text',
      required: false,
      group: 'Bank Account',
      placeholder: 'GB00XXXX00000000000000',
      hint: 'Optional. Used with SWIFT for international transfers.',
    });
  }
  fields.push({
    key: 'swift_code',
    label: 'SWIFT Code (BIC)',
    type: 'text',
    required: false,
    group: 'Bank Account',
    placeholder: '8 or 11 characters',
    hint: 'Optional. Used for international transfers.',
    maxLength: 11,
  });
  return fields;
}

function parseCountry(raw: unknown, fallbackCode?: string): InvoiceCountryConfig | null {
  if (typeof raw === 'string') {
    const code = fallbackCode ?? raw;
    return { code, label: raw, fields: [] };
  }
  if (Array.isArray(raw)) {
    if (!fallbackCode) return null;
    return { code: fallbackCode, label: fallbackCode, fields: parseFields(raw) };
  }
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const code = readString(o, ['code', 'country', 'value', 'key']) ?? fallbackCode;
  if (!code) return null;
  const label = readString(o, ['label', 'name', 'title', 'country_name', 'countryName']) ?? code;
  const supplierTaxLabel = readString(o, ['supplier_tax_label', 'supplierTaxLabel']);
  const parsedFields = parseFields(o.fields ?? o.form ?? o.items ?? o.columns ?? o.schema);
  const fields = parsedFields.length > 0 ? parsedFields : fieldsFromCountryMeta(o);
  return { code, label, fields, supplierTaxLabel };
}

function parseCountries(raw: unknown): InvoiceCountryConfig[] {
  if (Array.isArray(raw)) {
    return raw.map((item) => parseCountry(item)).filter((item): item is InvoiceCountryConfig => item !== null);
  }
  if (!raw || typeof raw !== 'object') return [];
  return Object.entries(raw as Record<string, unknown>)
    .map(([code, value]) => parseCountry(value, code))
    .filter((item): item is InvoiceCountryConfig => item !== null);
}

function collectScalars(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (META_KEYS.has(key) || key.startsWith('_')) continue;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      out[key] = String(value);
    }
  }
  return out;
}

/** 解析 `POST /system/team/get_invoice_info` 的 `data`。国家与字段来自 `_countries`。 */
export function parseInvoiceSettings(data: unknown): InvoiceSettings | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const o = data as Record<string, unknown>;
  const countries = parseCountries(o._countries ?? o.countries ?? o.country_list ?? o.countryList);
  const values = {
    ...collectScalars(o),
    ...collectScalars(o.invoice_info ?? o.invoiceInfo),
  };
  const country = readString(o, ['country', 'country_code', 'countryCode'])
    ?? readString(
      (o.invoice_info && typeof o.invoice_info === 'object' ? o.invoice_info : {}) as Record<string, unknown>,
      ['country', 'country_code', 'countryCode'],
    )
    ?? countries[0]?.code
    ?? '';
  if (countries.length === 0 && !country && Object.keys(values).length === 0) return null;
  return { country, values, countries };
}

export async function fetchInvoiceSettings(): Promise<InvoiceSettings> {
  const res = await http.post<{ error?: boolean; message?: string; data?: unknown }>(
    '/system/team/get_invoice_info',
    undefined,
    { skipApiEnvelopeModal: true },
  );
  if (res.error === true) {
    throw new ApiRequestError(
      typeof res.message === 'string' ? res.message : 'Failed to load invoice settings.',
    );
  }
  const parsed = parseInvoiceSettings(res.data);
  if (!parsed) {
    throw new ApiRequestError('Failed to load invoice settings.');
  }
  return parsed;
}

export async function postSaveInvoiceInfo(
  body: Record<string, string>,
): Promise<{ error: boolean; message: string }> {
  const failFallback = 'Failed to save invoice settings.';
  const okFallback = 'Invoice settings saved.';
  try {
    const res = await http.post<{ error?: boolean; message?: string }>(
      '/system/team/save_invoice_info',
      body,
      { skipApiEnvelope: true },
    );
    if (res.error === true) {
      return { error: true, message: envelopeMessage(res, failFallback) };
    }
    return { error: false, message: envelopeMessage(res, okFallback) };
  } catch (e) {
    const message = e instanceof ApiRequestError ? e.message : failFallback;
    return { error: true, message };
  }
}
